/**
 * Knowledge base, agent-facing — Step 33. Same two-pane anatomy as the
 * catalogue admin (rail of categories/folders + a list, then a full-page
 * editor), because they are the same shape of problem and agents should not
 * have to learn two screens.
 *
 * The visibility chip on every folder is deliberately loud: it is the
 * highest-severity setting in the product, and "Anyone" means the public
 * internet. Article bodies are DRAFT-FIRST (free-form text, the absolute
 * rule); only the status switch commits on its own.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowDownWideNarrow,
  ArrowLeft,
  BookOpen,
  FolderPlus,
  Globe,
  Lock,
  Plus,
  Search,
  Trash2,
  Users,
  History,
} from 'lucide-react';
import {
  kbApi,
  KB_VISIBILITY,
  KB_VISIBILITY_LABELS,
  type KbVisibility,
  type KbArticleRow,
} from '../features/kb/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { ArticleView } from '../features/kb/ArticleView';
import { ArticleEditor } from '../features/kb/ArticleEditor';
import { ArticleRail, fromDateInput, toDateInput, type RailDraft } from '../features/kb/ArticleRail';
import { VersionHistory } from '../features/kb/VersionHistory';
import { ticketsApi } from '../features/tickets/api';
import { qk } from '../lib/queryClient';
import { useUrlState } from '../lib/urlState';
import { useDirty, useConfirmDiscard } from '../lib/dirty';
import { relativeTime } from '../lib/time';
import { cn } from '../lib/utils';
import { useConfirm } from '../lib/confirm';

const VIS_ICON: Record<KbVisibility, typeof Globe> = {
  public: Globe,
  signed_in: Users,
  agents: Lock,
};

function VisibilityChip({ v, compact }: { v: KbVisibility; compact?: boolean }) {
  const Icon = VIS_ICON[v];
  return (
    <span
      className={cn(
        'flex shrink-0 items-center gap-1 rounded text-[10px] font-medium',
        compact ? 'p-0.5' : 'px-1.5 py-0.5',
        // "Anyone" is the only one that reaches the public internet, so it is
        // the only one that gets a saturated colour.
        v === 'public'
          ? 'bg-amber-500/15 text-amber-700 dark:text-amber-400'
          : 'bg-muted text-muted-foreground',
      )}
      // In the rail the icon alone carries it — the folder's NAME is what
      // someone is scanning for, and a full chip beside it left "Office 36…".
      title={`${KB_VISIBILITY_LABELS[v].label} — ${KB_VISIBILITY_LABELS[v].help}`}
      aria-label={`Visible to ${KB_VISIBILITY_LABELS[v].label}`}
    >
      <Icon className="h-3 w-3" />
      {!compact && KB_VISIBILITY_LABELS[v].label}
    </span>
  );
}

export function KbPage() {
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const [folderId, setFolderId] = useUrlState('folder', null);
  const [statusFilter, setStatusFilter] = useUrlState('status', 'all');
  const [search, setSearch] = useUrlState('q', '');
  const [articleId, setArticleId] = useUrlState('article', null);
  // Kai: "the backend seeing it, then purposely going into the actual editor
  // to change things". Landing straight in an editor makes every visit an
  // accidental-edit risk on content other people rely on.
  const [editing, setEditing] = useUrlState('edit', null);
  const [sortBy, setSortBy] = useUrlState('sort', 'updated');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const tree = useQuery({ queryKey: qk.kbTree, queryFn: kbApi.tree });
  const peopleQuery = useQuery({ queryKey: qk.people, queryFn: ticketsApi.people });
  const listParams = JSON.stringify({ folderId, statusFilter, search });
  const list = useQuery({
    queryKey: qk.kbArticles.list(listParams),
    queryFn: () =>
      kbApi.articles({
        ...(folderId ? { folder: folderId } : {}),
        // Step 122 — "due for review" is a client-side cut of published rows.
        ...(statusFilter === 'published' || statusFilter === 'draft'
          ? { status: statusFilter }
          : statusFilter === 'review'
            ? { status: 'published' }
            : {}),
        ...(search.trim() ? { q: search.trim() } : {}),
      }),
    enabled: !articleId,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: qk.kbTree });
    void queryClient.invalidateQueries({ queryKey: qk.kbArticles.all });
  };

  const [addingCat, setAddingCat] = useState(false);
  const [catName, setCatName] = useState('');
  const [addingFolderIn, setAddingFolderIn] = useState<string | null>(null);
  const [folderName, setFolderName] = useState('');
  const [treeError, setTreeError] = useState<string | null>(null);

  if (articleId) {
    return (
      <ArticleScreen
        id={articleId}
        editing={editing === '1'}
        onEdit={() => setEditing('1')}
        onDoneEditing={() => setEditing(null)}
        onClose={() => {
          setEditing(null);
          setArticleId(null);
        }}
        onSaved={invalidate}
      />
    );
  }

  if (tree.isPending)
    return (
      <div className="max-w-5xl space-y-3 p-6">
        <Skeleton className="h-6 w-44" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  if (tree.isError)
    return (
      <div className="m-6 max-w-3xl space-y-2 rounded-md border border-destructive/30 bg-destructive/10 p-3">
        <p className="text-sm font-medium text-destructive">Could not load the knowledge base</p>
        <p className="text-xs text-destructive/80">{(tree.error as Error).message}</p>
        <button
          onClick={() => tree.refetch()}
          className="text-xs text-destructive/80 underline hover:text-destructive"
        >
          Retry
        </button>
      </div>
    );

  const categories = tree.data!.categories;
  const allFolders = categories.flatMap((c) => c.folders);
  const selectedFolder = allFolders.find((f) => f.id === folderId);

  // The reference's sortable columns. Sorting client-side is honest here:
  // the whole filtered list is already in hand, and paging it server-side
  // would be a bigger change than the screen currently needs.
  const rows = [...(list.data?.entries ?? [])]
    .filter((a) => statusFilter !== 'review' || (a.reviewAt != null && a.reviewAt <= Date.now()))
    .sort((a, b) => {
      switch (sortBy) {
        case 'title':
          return a.title.localeCompare(b.title);
        case 'helpful':
          return b.helpful - a.helpful;
        case 'notHelpful':
          return b.notHelpful - a.notHelpful;
        case 'inserted':
          return b.insertedCount - a.insertedCount;
        case 'author':
          return (a.authorName ?? '').localeCompare(b.authorName ?? '');
        case 'folder':
          return a.folderName.localeCompare(b.folderName);
        default:
          return b.updatedAt - a.updatedAt;
      }
    });
  const allSelected = rows.length > 0 && rows.every((r) => selectedIds.has(r.id));
  const staffList = (peopleQuery.data?.entries ?? []).filter((p) => p.kind !== 'requester');

  async function bulk(action: 'publish' | 'draft' | 'delete' | 'author', authorId?: string | null) {
    if (
      action === 'delete' &&
      !(await confirm({
        title: `Delete ${selectedIds.size} article${selectedIds.size === 1 ? '' : 's'}?`,
        body: 'This cannot be undone.',
        destructive: true,
        confirmLabel: 'Delete',
      }))
    )
      return;
    setBulkBusy(true);
    for (const id of selectedIds) {
      if (action === 'delete') await kbApi.deleteArticle(id);
      else if (action === 'author') await kbApi.updateArticle(id, { authorId: authorId ?? null });
      else await kbApi.updateArticle(id, { status: action === 'publish' ? 'published' : 'draft' });
    }
    setBulkBusy(false);
    setSelectedIds(new Set());
    invalidate();
  }

  return (
    // Full bleed on purpose: a three-pane browser; each pane pads and scrolls
    // itself, so padding out here would inset the dividers.
    <div className="flex h-full min-h-0">
      <aside className="flex w-64 shrink-0 flex-col overflow-y-auto border-r p-3">
        <button
          onClick={() => setFolderId(null)}
          className={cn(
            'mb-2 flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-sm',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            !folderId
              ? 'bg-accent font-medium text-accent-foreground'
              : 'text-muted-foreground hover:bg-accent/60 hover:text-foreground',
          )}
        >
          <BookOpen className="h-4 w-4" /> All articles
        </button>

        {categories.map((cat) => (
          <div key={cat.id} className="mb-2">
            <div className="group flex items-center gap-1 px-1 py-1">
              <span className="min-w-0 flex-1 truncate text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                {cat.name}
              </span>
              <button
                onClick={async () => {
                  setAddingFolderIn(cat.id);
                  setFolderName('');
                }}
                className="rounded p-0.5 text-muted-foreground opacity-0 hover:text-foreground focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring group-hover:opacity-100"
                aria-label={`Add a folder in ${cat.name}`}
                title="New folder"
              >
                <FolderPlus className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={async () => {
                  setTreeError(null);
                  const res = await kbApi.deleteCategory(cat.id);
                  if (res.ok) invalidate();
                  else setTreeError(res.error ?? 'Could not delete');
                }}
                className="rounded p-0.5 text-muted-foreground opacity-0 hover:text-destructive focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring group-hover:opacity-100"
                aria-label={`Delete ${cat.name}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
            {cat.folders.map((f) => (
              <button
                key={f.id}
                onClick={() => setFolderId(f.id)}
                className={cn(
                  'flex w-full items-center gap-1.5 rounded-md px-2.5 py-1.5 text-left text-sm',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  folderId === f.id
                    ? 'bg-accent font-medium text-accent-foreground'
                    : 'text-muted-foreground hover:bg-accent/60 hover:text-foreground',
                )}
              >
                <span className="min-w-0 flex-1 truncate">{f.name}</span>
                <VisibilityChip v={f.visibility} compact />
                <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{f.published}</span>
              </button>
            ))}
            {addingFolderIn === cat.id && (
              <div className="px-1 py-1">
                <Input
                  autoFocus
                  value={folderName}
                  onChange={(e) => setFolderName(e.target.value)}
                  onKeyDown={async (e) => {
                    if (e.key === 'Escape') setAddingFolderIn(null);
                    if (e.key === 'Enter' && folderName.trim()) {
                      const res = await kbApi.createFolder({ categoryId: cat.id, name: folderName.trim() });
                      if (res.ok) {
                        setAddingFolderIn(null);
                        invalidate();
                      } else setTreeError(res.error ?? 'Could not create the folder');
                    }
                  }}
                  onBlur={() => {
                    if (!folderName.trim()) setAddingFolderIn(null);
                  }}
                  placeholder="Folder name"
                  className="h-7 text-sm"
                />
                <p className="mt-1 text-[10px] text-muted-foreground">
                  New folders start as Agents only — change it once you mean to.
                </p>
              </div>
            )}
          </div>
        ))}

        {addingCat ? (
          <Input
            autoFocus
            value={catName}
            onChange={(e) => setCatName(e.target.value)}
            onKeyDown={async (e) => {
              if (e.key === 'Escape') setAddingCat(false);
              if (e.key === 'Enter' && catName.trim()) {
                const res = await kbApi.createCategory({ name: catName.trim() });
                if (res.ok) {
                  setAddingCat(false);
                  setCatName('');
                  invalidate();
                } else setTreeError(res.error ?? 'Could not create the category');
              }
            }}
            onBlur={() => {
              if (!catName.trim()) setAddingCat(false);
            }}
            placeholder="Category name"
            className="h-8 text-sm"
          />
        ) : (
          <button
            onClick={() => setAddingCat(true)}
            className="mt-1 flex items-center gap-1.5 rounded px-2.5 py-1.5 text-sm text-brand hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Plus className="h-4 w-4" /> New category
          </button>
        )}
        {treeError && <p className="mt-2 px-1 text-xs text-destructive">{treeError}</p>}
      </aside>

      <main className="min-w-0 flex-1 overflow-y-auto p-6">
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div className="min-w-0">
            <h1 className="truncate text-lg font-semibold">{selectedFolder?.name ?? 'All articles'}</h1>
            {!selectedFolder && (
              <p className="mt-0.5 text-xs text-muted-foreground">
                {allFolders.length} folder{allFolders.length === 1 ? '' : 's'} ·{' '}
                {allFolders.reduce((n, f) => n + f.published + (f.drafts ?? 0), 0)} articles
              </p>
            )}
            {selectedFolder && (
              <div className="mt-1 flex items-center gap-2">
                <VisibilityChip v={selectedFolder.visibility} />
                <Select
                  value={selectedFolder.visibility}
                  onChange={async (e) => {
                    const next = e.target.value as KbVisibility;
                    if (
                      next === 'public' &&
                      !(await confirm({
                        title: `Make “${selectedFolder.name}” visible to anyone on the internet?`,
                        body: 'Everything published in it becomes public, without signing in.',
                        destructive: true,
                        confirmLabel: 'Make it public',
                      }))
                    )
                      return;
                    const res = await kbApi.updateFolder(selectedFolder.id, { visibility: next });
                    if (res.ok) invalidate();
                    else setTreeError(res.error ?? 'Could not change visibility');
                  }}
                  className="h-7 w-44 text-xs"
                >
                  {KB_VISIBILITY.map((v) => (
                    <option key={v} value={v}>
                      {KB_VISIBILITY_LABELS[v].label}
                    </option>
                  ))}
                </Select>
                <span className="text-xs text-muted-foreground">
                  {KB_VISIBILITY_LABELS[selectedFolder.visibility].help}
                </span>
              </div>
            )}
          </div>
          <div className="flex-1" />
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search articles"
              className="h-9 w-56 pl-8"
            />
          </div>
          <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-9 w-36">
            <option value="all">All statuses</option>
            <option value="published">Published</option>
            <option value="draft">Drafts</option>
            <option value="review">Due for review</option>
          </Select>
          <Button
            size="sm"
            disabled={!folderId}
            title={folderId ? undefined : 'Pick a folder first'}
            onClick={async () => {
              const res = await kbApi.createArticle({ folderId: folderId!, title: 'Untitled article' });
              if (res.ok && res.body) {
                invalidate();
                setArticleId(res.body.id);
              }
            }}
          >
            <Plus /> New article
          </Button>
        </div>

        {list.isPending && <Skeleton className="h-64 w-full" />}
        {list.isError && (
          <div className="space-y-2 rounded-md border border-destructive/30 bg-destructive/10 p-3">
            <p className="text-sm font-medium text-destructive">Could not load articles</p>
            <button
              onClick={() => list.refetch()}
              className="text-xs text-destructive/80 underline hover:text-destructive"
            >
              Retry
            </button>
          </div>
        )}
        {selectedIds.size > 0 && (
          <div className="mb-3 flex flex-wrap items-center gap-2 rounded-md border border-brand/30 bg-brand/5 p-2.5 text-sm">
            <span className="font-medium">
              {selectedIds.size} article{selectedIds.size === 1 ? '' : 's'} selected
            </span>
            <div className="flex-1" />
            {/* Imported articles have no author, and setting 22 of them one
                at a time is not a workflow — so it is a bulk action too. */}
            <Select
              value=""
              disabled={bulkBusy}
              onChange={(e) => {
                if (!e.target.value) return;
                void bulk('author', e.target.value === '__none__' ? null : e.target.value);
              }}
              className="h-8 w-40 text-xs"
              aria-label="Set the author on the selected articles"
            >
              <option value="">Set author…</option>
              <option value="__none__">No author</option>
              {staffList.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name ?? p.email}
                </option>
              ))}
            </Select>
            <Button size="sm" variant="outline" disabled={bulkBusy} onClick={() => void bulk('publish')}>
              Publish
            </Button>
            <Button size="sm" variant="outline" disabled={bulkBusy} onClick={() => void bulk('draft')}>
              Move to drafts
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="border-destructive/40 text-destructive hover:bg-destructive/10"
              disabled={bulkBusy}
              onClick={() => void bulk('delete')}
            >
              <Trash2 /> Delete
            </Button>
            <button
              onClick={() => setSelectedIds(new Set())}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              Clear
            </button>
          </div>
        )}

        {list.data &&
          (rows.length === 0 ? (
            <div className="rounded-lg border p-10 text-center text-sm text-muted-foreground">
              {search || statusFilter !== 'all'
                ? 'Nothing matches this filter.'
                : folderId
                  ? 'No articles in this folder yet.'
                  : 'No articles yet — pick a folder and write the first one.'}
            </div>
          ) : (
            <div className="overflow-hidden rounded-lg border bg-card">
              <div
                className={cn(
                  // Columns drop out as the pane narrows rather than crushing
                  // the title to 40px, which is what fixed widths did here:
                  // this page carries the app sidebar AND a category rail.
                  GRID_COLS,
                  'grid gap-2 border-b bg-muted/30 px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground',
                )}
              >
                <span className="flex items-center">
                  <input
                    type="checkbox"
                    aria-label="Select all articles"
                    checked={allSelected}
                    onChange={() => setSelectedIds(allSelected ? new Set() : new Set(rows.map((r) => r.id)))}
                    className="h-3.5 w-3.5 accent-[hsl(var(--brand))]"
                  />
                </span>
                <SortHeader label="Title" col="title" sortBy={sortBy} onSort={setSortBy} />
                <span>Status</span>
                <SortHeader
                  label="Helpful"
                  col="helpful"
                  sortBy={sortBy}
                  onSort={setSortBy}
                  align="right"
                  className="hidden lg:flex"
                />
                <SortHeader
                  label="Not"
                  col="notHelpful"
                  sortBy={sortBy}
                  onSort={setSortBy}
                  align="right"
                  className="hidden lg:flex"
                />
                <SortHeader
                  label="Used"
                  col="inserted"
                  sortBy={sortBy}
                  onSort={setSortBy}
                  align="right"
                  className="hidden lg:flex"
                />
                <SortHeader
                  label="Author"
                  col="author"
                  sortBy={sortBy}
                  onSort={setSortBy}
                  className="hidden xl:flex"
                />
                <SortHeader label="Updated" col="updated" sortBy={sortBy} onSort={setSortBy} align="right" />
              </div>
              {rows.map((a: KbArticleRow) => (
                <div
                  key={a.id}
                  role="row"
                  className={cn(
                    GRID_COLS,
                    'grid w-full cursor-pointer items-center gap-2 border-b px-4 py-2.5 text-left last:border-b-0 hover:bg-accent/40',
                  )}
                  onClick={() => setArticleId(a.id)}
                >
                  <span className="flex items-center" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      aria-label={`Select ${a.title}`}
                      checked={selectedIds.has(a.id)}
                      onChange={() =>
                        setSelectedIds((prev) => {
                          const next = new Set(prev);
                          if (next.has(a.id)) next.delete(a.id);
                          else next.add(a.id);
                          return next;
                        })
                      }
                      className="h-3.5 w-3.5 accent-[hsl(var(--brand))]"
                    />
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium">{a.title}</span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {!folderId && a.folderName}
                      {!folderId && a.authorName ? ' · ' : ''}
                      {/* The author rides here while its own column is hidden. */}
                      <span className="xl:hidden">{a.authorName ?? ''}</span>
                    </span>
                  </span>
                  <span>
                    {a.status === 'published' ? (
                      <Badge variant="outline" className="gap-1.5">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Published
                      </Badge>
                    ) : (
                      <Badge className="border-transparent bg-amber-500/15 text-amber-700 dark:text-amber-400">
                        Draft
                      </Badge>
                    )}
                    {a.reviewAt != null && a.reviewAt <= Date.now() && (
                      <Badge
                        className="ml-1 border-transparent bg-amber-500/15 text-amber-700 dark:text-amber-400"
                        title="Review date passed"
                      >
                        Review due
                      </Badge>
                    )}
                  </span>
                  <span className="hidden text-right text-xs tabular-nums text-muted-foreground lg:block">
                    {a.helpful}
                  </span>
                  <span className="hidden text-right text-xs tabular-nums text-muted-foreground lg:block">
                    {a.notHelpful}
                  </span>
                  <span className="hidden text-right text-xs tabular-nums text-muted-foreground lg:block">
                    {a.insertedCount}
                  </span>
                  <span className="hidden truncate text-xs text-muted-foreground xl:block">
                    {a.authorName ?? '—'}
                  </span>
                  <span className="text-right text-xs text-muted-foreground">
                    {relativeTime(a.updatedAt)}
                  </span>
                </div>
              ))}
            </div>
          ))}
      </main>
    </div>
  );
}

/** Draft-first by the book: title and body are free-form text, so nothing
 *  leaves this editor until Save, and typed content registers as dirty. */
const GRID_COLS =
  'grid-cols-[28px_minmax(0,1fr)_104px_96px] lg:grid-cols-[28px_minmax(0,1fr)_104px_52px_52px_64px_96px] xl:grid-cols-[28px_minmax(0,1fr)_104px_52px_52px_64px_128px_96px]';

function SortHeader({
  label,
  col,
  sortBy,
  onSort,
  align,
  className,
}: {
  label: string;
  col: string;
  sortBy: string;
  onSort: (c: string) => void;
  align?: 'right';
  className?: string;
}) {
  const active = sortBy === col;
  return (
    <button
      onClick={() => onSort(col)}
      className={cn(
        'flex items-center gap-1 truncate text-[11px] font-semibold uppercase tracking-wider',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        align === 'right' && 'justify-end',
        active ? 'text-foreground' : 'text-muted-foreground hover:text-foreground',
        className,
      )}
      title={`Sort by ${label}`}
    >
      {label}
      {active && <ArrowDownWideNarrow className="h-3 w-3" />}
    </button>
  );
}

/**
 * One screen for an article, in two modes. Reading is the default; editing is
 * entered on purpose (Kai) and leaves the same way, so the URL always says
 * which one you are in and Back behaves.
 */
function ArticleScreen({
  id,
  editing,
  onEdit,
  onDoneEditing,
  onClose,
  onSaved,
}: {
  id: string;
  editing: boolean;
  onEdit: () => void;
  onDoneEditing: () => void;
  onClose: () => void;
  onSaved: () => void;
}) {
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: qk.kbArticle(id),
    queryFn: () => kbApi.article(id),
  });
  const tree = useQuery({ queryKey: qk.kbTree, queryFn: kbApi.tree });
  // Staff who can be named as an author. Imported articles have none — the
  // export's Freshdesk user ids do not resolve to our users — so this has to
  // be a real, settable field rather than a read-out.
  const people = useQuery({ queryKey: qk.people, queryFn: ticketsApi.people });

  const [draft, setDraft] = useState<{ title: string; bodyHtml: string } | null>(null);
  const [rail, setRail] = useState<RailDraft | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const active = draft ?? (data ? { title: data.title, bodyHtml: data.bodyHtml ?? '' } : null);
  const activeRail =
    rail ??
    (data
      ? {
          folderId: data.folderId,
          articleType: data.articleType,
          keywords: data.keywords ?? '',
          authorId: data.authorId ?? null,
          reviewOn: toDateInput(data.reviewAt),
        }
      : null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const dirty =
    !!data &&
    !!active &&
    !!activeRail &&
    (active.title !== data.title ||
      active.bodyHtml !== (data.bodyHtml ?? '') ||
      activeRail.folderId !== data.folderId ||
      activeRail.articleType !== data.articleType ||
      activeRail.keywords !== (data.keywords ?? '') ||
      activeRail.authorId !== (data.authorId ?? null) ||
      activeRail.reviewOn !== toDateInput(data.reviewAt));
  const clearDirty = useDirty(editing && dirty);
  const confirmDiscard = useConfirmDiscard();

  const save = useMutation({
    mutationFn: (status?: string) =>
      kbApi.updateArticle(id, {
        title: active!.title.trim() || 'Untitled article',
        bodyHtml: active!.bodyHtml,
        folderId: activeRail!.folderId,
        articleType: activeRail!.articleType,
        keywords: activeRail!.keywords,
        authorId: activeRail!.authorId,
        // Step 122 — the review date, and the `updatedAt` this editor loaded
        // so a save over somebody else's is refused rather than silent.
        reviewAt: fromDateInput(activeRail!.reviewOn),
        expectedUpdatedAt: data!.updatedAt,
        ...(status ? { status } : {}),
      }),
    onSuccess: (res) => {
      if (!res.ok) {
        setSaveError(res.error ?? 'Saving failed');
        return;
      }
      // `setRail` is URL state, so this close is a navigation; the guard has
      // to be retired first or Save trips it.
      clearDirty();
      setDraft(null);
      setRail(null);
      void queryClient.invalidateQueries({ queryKey: qk.kbArticle(id) });
      onSaved();
    },
  });

  if (isPending || tree.isPending) return <Skeleton className="m-6 h-96 w-full max-w-4xl" />;
  if (isError)
    return (
      <div className="m-6 max-w-3xl space-y-2 rounded-md border border-destructive/30 bg-destructive/10 p-3">
        <p className="text-sm font-medium text-destructive">Could not load the article</p>
        <p className="text-xs text-destructive/80">{(error as Error).message}</p>
        <button
          onClick={() => refetch()}
          className="text-xs text-destructive/80 underline hover:text-destructive"
        >
          Retry
        </button>
      </div>
    );

  const published = data!.status === 'published';
  const categories = tree.data?.categories ?? [];
  const staff = (people.data?.entries ?? []).filter((p) => p.kind !== 'requester');

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="sticky top-0 z-20 flex shrink-0 flex-wrap items-center gap-3 border-b bg-background/95 px-6 py-3 backdrop-blur">
        <button
          onClick={async () => {
            if (editing && dirty && !(await confirmDiscard())) return;
            onClose();
          }}
          className="rounded text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label="Back to the knowledge base"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <h1 className="min-w-0 max-w-md truncate text-base font-semibold">
          {editing ? `Edit — ${active!.title || 'Untitled article'}` : data!.title}
        </h1>
        {!editing &&
          (published ? (
            <Badge variant="outline" className="gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Published
            </Badge>
          ) : (
            <Badge className="border-transparent bg-amber-500/15 text-amber-700 dark:text-amber-400">
              Draft
            </Badge>
          ))}
        {!editing && data!.reviewAt != null && data!.reviewAt <= Date.now() && (
          <Badge
            className="border-transparent bg-amber-500/15 text-amber-700 dark:text-amber-400"
            title="The review date on this article has passed"
          >
            Due for review
          </Badge>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setHistoryOpen(true)}
          title="Every version of this article, with what changed"
        >
          <History /> History
        </Button>
        {historyOpen && (
          <VersionHistory article={data!} open onClose={() => setHistoryOpen(false)} canRestore />
        )}
        <div className="flex-1" />
        {editing && dirty && (
          <span className="text-xs text-amber-600 dark:text-amber-500">Unsaved changes</span>
        )}
        {saveError && <span className="max-w-xs truncate text-xs text-destructive">{saveError}</span>}
        {editing && (
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                if (dirty && !(await confirmDiscard())) return;
                setDraft(null);
                setRail(null);
                setSaveError(null);
                onDoneEditing();
              }}
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={save.isPending}
              onClick={() => save.mutate(published ? 'draft' : 'published')}
            >
              {published ? 'Unpublish' : 'Publish'}
            </Button>
            <Button size="sm" disabled={!dirty || save.isPending} onClick={() => save.mutate(undefined)}>
              Save
            </Button>
          </>
        )}
      </div>

      {editing ? (
        <div className="flex min-h-0 flex-1 gap-6 overflow-y-auto p-6">
          <div className="min-w-0 flex-1 space-y-4">
            <div>
              <label className="mb-1.5 block text-xs font-medium text-muted-foreground">Title</label>
              <Input
                value={active!.title}
                onChange={(e) => setDraft({ ...active!, title: e.target.value })}
                className="h-11 text-base font-semibold"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium text-muted-foreground">Article</label>
              <ArticleEditor
                articleId={id}
                value={active!.bodyHtml}
                onChange={(v) => setDraft({ ...active!, bodyHtml: v })}
              />
            </div>
            <div className="flex justify-end border-t pt-4">
              <Button
                variant="outline"
                className="border-destructive/40 text-destructive hover:bg-destructive/10"
                onClick={async () => {
                  if (
                    !(await confirm({
                      title: `Delete “${data!.title}”?`,
                      body: 'This cannot be undone.',
                      destructive: true,
                      confirmLabel: 'Delete',
                    }))
                  )
                    return;
                  const res = await kbApi.deleteArticle(id);
                  if (res.ok) {
                    onSaved();
                    onClose();
                  } else setSaveError(res.error ?? 'Could not delete');
                }}
              >
                <Trash2 /> Delete article
              </Button>
            </div>
          </div>
          <ArticleRail
            article={data!}
            categories={categories}
            people={staff}
            draft={activeRail!}
            onChange={(next) => setRail(next)}
          />
        </div>
      ) : (
        <ArticleView article={data!} categories={categories} people={staff} onEdit={onEdit} />
      )}
    </div>
  );
}
